import axios, { AxiosInstance, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';

import { AppRoutes } from 'routes/routes.enums';
import { BorrowerFraudErrorMessage } from 'enums/common.enums';

import { SessionInfoDto, SignInDto } from './auth/authApi.types';

declare module 'axios' {
  // @ts-ignore
  interface AxiosResponse<T = any> extends Promise<T> {}

  interface InternalAxiosRequestConfig {
    retriedAfterRefresh?: boolean;
  }
}

const TOKEN_INVALID_OR_EXPIRED_MESSAGE = 'Token is invalid or expired';
let isRefreshTokenRequestInProgress = false;
let requestsQueueForRefreshTokenProcess: any[] = [];

export abstract class HttpClient {
  protected readonly instance: AxiosInstance;

  public constructor(baseURL: string) {
    this.instance = axios.create({
      baseURL: baseURL,
    });

    this.initializeResponseInterceptor();
  }

  private initializeResponseInterceptor = () => {
    this.instance.interceptors.response.use(this.handleResponse, this.handleResponseError);
    this.instance.interceptors.request.use(this.handleRequest, this.handleRequestError);
  };

  private handleRequest = (
    config: InternalAxiosRequestConfig<any>,
  ): InternalAxiosRequestConfig<any> | Promise<InternalAxiosRequestConfig<any>> => {
    let accessToken = localStorage.getItem('token');

    if (accessToken) {
      // @ts-ignore
      config.headers.authorization = `Bearer ${accessToken}`;
    }

    return config;
  };

  private handleRequestError = (error: AxiosError): Promise<AxiosError> => {
    return Promise.reject(error);
  };

  private handleResponse = (response: AxiosResponse) => response;

  private handleResponseError = async (error: AxiosError<any>) => {
    const originalRequest = error?.config;
    const errorStatus = error?.response?.status;
    const errorMessage = error?.response?.data?.message;

    if (!originalRequest) {
      return Promise.reject(error?.response?.data);
    }

    if (this.checkIsLoginWithCredentialsRequest(originalRequest)) {
      return Promise.reject(error?.response?.data);
    }

    if (this.checkIsRefreshTokenRequest(originalRequest)) {
      this.clearStateAfterRefreshTokenFailed();

      return Promise.reject(error?.response?.data);
    }

    // try to refresh token only if it's a 'token expired' error
    if (errorStatus === 401 && errorMessage === TOKEN_INVALID_OR_EXPIRED_MESSAGE) {
      if (originalRequest.retriedAfterRefresh) {
        this.clearStateAfterRefreshTokenFailed();

        return Promise.reject(error?.response?.data);
      }

      originalRequest.retriedAfterRefresh = true;

      if (isRefreshTokenRequestInProgress) {
        // While refresh token request is in progress, other requests should wait it and should be added to the queue
        return new Promise(function (resolve, reject) {
          requestsQueueForRefreshTokenProcess.push({ resolve: resolve, reject: reject });
        }).then(() => {
          return this.instance(originalRequest);
        });
      } else {
        await this.tryRefreshToken();

        return this.instance(originalRequest);
      }
    }

    if (errorStatus === 403) {
      if (errorMessage === BorrowerFraudErrorMessage.Fraud) {
        window.location.replace(AppRoutes.Error);

        return this.instance(originalRequest);
      }

      if (errorMessage === BorrowerFraudErrorMessage.BankFraud) {
        window.location.replace(AppRoutes.NotQualified);

        return this.instance(originalRequest);
      }
    }

    return Promise.reject(error?.response?.data);
  };

  private checkIsLoginWithCredentialsRequest = (originalRequest: InternalAxiosRequestConfig) => {
    if (!originalRequest.url?.includes('/login')) {
      return false;
    }

    const requestData: SignInDto = JSON.parse(originalRequest.data);

    return !!requestData.password;
  };

  private checkIsRefreshTokenRequest = (originalRequest: InternalAxiosRequestConfig) => {
    if (!originalRequest.url?.includes('/login')) {
      return false;
    }

    const requestData: SignInDto = JSON.parse(originalRequest.data);

    return !!requestData.refreshToken;
  };

  private tryRefreshToken = async () => {
    isRefreshTokenRequestInProgress = true;

    const refreshToken = localStorage.getItem('refreshToken')!;

    if (!refreshToken) {
      this.clearStateAfterRefreshTokenFailed();

      return;
    }

    const loginResult: SessionInfoDto = await this.instance
      .post(process.env.REACT_APP_API_URL + '/account/login', { refreshToken: refreshToken })
      .then((response: AxiosResponse<any>) => response.data);

    localStorage.setItem('token', loginResult!.accessToken);
    localStorage.setItem('refreshToken', loginResult!.refreshToken);

    isRefreshTokenRequestInProgress = false;
    this.processRequestsQueueForRefreshTokenProcess();
    requestsQueueForRefreshTokenProcess = [];
  };

  private processRequestsQueueForRefreshTokenProcess = () => {
    requestsQueueForRefreshTokenProcess.forEach((request) => {
      request.resolve();
    });
  };

  private clearStateAfterRefreshTokenFailed = () => {
    isRefreshTokenRequestInProgress = false;
    requestsQueueForRefreshTokenProcess = [];

    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    sessionStorage.clear();
    window.location.reload();
  };
}
